import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  ChemistryPreferences,
  DEFAULT_MIN_SIMILARITY_CUTOFF,
  MOLECULE_CHEM_SEARCH_MODAL_TITLE,
  REACTION_CHEM_SEARCH_DESCRIPTION,
  REACTION_CHEM_SEARCH_MODAL_TITLE,
} from 'chemistry/ChemistryConstants';
import {
  getDefaultChemSearchOperator,
  getDefaultReactionComponentRole,
  isReactionChemSearch,
  ToggleHighlightParams,
} from 'chemistry/ChemSearchUtils';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import preferences from 'Preferences';
import {
  ChemicalStructureSearchOperator,
  ChemicalStructureSearchParams,
  Operator,
  SearchParams,
} from 'search/SearchConstants';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import {GRAY_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';

import ChemSearchModalOptions from './ChemSearchModalOptions';

interface ChemSearchModalProps {
  initialMol: string | null;
  onRequestSubmit: (chemSearchParams: ChemicalStructureSearchParams) => void;
  onRequestClose: () => void;
  toggleHighlightParams: ToggleHighlightParams;
  searchParams: SearchParams;
}

export default function ChemSearchModal({
  initialMol,
  onRequestSubmit,
  onRequestClose,
  toggleHighlightParams,
  searchParams,
}: ChemSearchModalProps): JSX.Element {
  const [mol, setMol] = useState<ChemicalStructureDrawerValue>(initialMol);
  const [highlightChecked, setHighlightChecked] = useState<boolean>(
    toggleHighlightParams.shouldHighlightSubstructure
  );
  const [chemSearchOperator, setChemSearchOperator] = useState<ChemicalStructureSearchOperator>(
    getDefaultChemSearchOperator(searchParams)
  );
  const [minSimilarity, setMinSimilarity] = useState<number>(
    preferences.get(ChemistryPreferences.MIN_SIMILARITY) ?? DEFAULT_MIN_SIMILARITY_CUTOFF
  );
  const [reactionComponentRole, setReactionComponentRole] = useState<ChemistryReactionRole | null>(() =>
    getDefaultReactionComponentRole(searchParams)
  );
  const isReactionSearch = isReactionChemSearch(searchParams);

  const savePreferencesAndSubmitSearchParams = (): void => {
    preferences.set(ChemistryPreferences.CHEM_SEARCH_TYPE, chemSearchOperator);
    if (toggleHighlightParams.showToggleHighlightOption) {
      toggleHighlightParams.setShouldHighlightSubstructure(highlightChecked);
    }
    const baseParams: ChemicalStructureSearchParams = {
      chemicalStructureQuery: mol,
      chemSearchOperator,
    };
    if (isReactionSearch) {
      baseParams.reactionComponentRole = reactionComponentRole;
    }
    if (chemSearchOperator === Operator.SIMILARITY) {
      preferences.set(ChemistryPreferences.MIN_SIMILARITY, minSimilarity);
      onRequestSubmit({
        ...baseParams,
        minSimilarity,
      });
    } else {
      onRequestSubmit(baseParams);
    }
  };

  return (
    <ExpandableModal
      bodyPadded={false}
      enableRedesign={true}
      showHeaderBorder={!isReactionSearch}
      title={isReactionSearch ? REACTION_CHEM_SEARCH_MODAL_TITLE : MOLECULE_CHEM_SEARCH_MODAL_TITLE}
      buttons={[
        {
          text: 'Search',
          submit: true,
          dataTestAttrs: {component: 'ChemSearchModal', element: 'submit'},
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={savePreferencesAndSubmitSearchParams}
      expandHeight={true}
      scrollable={true}
    >
      {isReactionSearch ? (
        <div className={css(styles.reactionSearchIntro)}>
          <p className={css(styles.reactionSearchDescription)}>{REACTION_CHEM_SEARCH_DESCRIPTION}</p>
        </div>
      ) : null}
      <div className={css(styles.chemicalDrawer)}>
        <ChemicalStructureDrawer
          onMoleculeChange={setMol}
          initialValue={mol}
          mode={ChemicalStructureDrawerMode.STANDARD}
        />
      </div>
      <div className={css(styles.searchOptionsContainer)}>
        <ChemSearchModalOptions
          highlightChecked={highlightChecked}
          setHighlightChecked={setHighlightChecked}
          chemSearchOperator={chemSearchOperator}
          setChemSearchOperator={setChemSearchOperator}
          minSimilarity={minSimilarity}
          setMinSimilarity={setMinSimilarity}
          toggleHighlightParams={toggleHighlightParams}
          isReactionSearch={isReactionSearch}
          reactionComponentRole={reactionComponentRole}
          setReactionComponentRole={setReactionComponentRole}
          className={css(styles.searchOptions)}
        />
      </div>
    </ExpandableModal>
  );
}

const styles = StyleSheet.create({
  reactionSearchIntro: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: '0 16px 16px',
  },
  reactionSearchDescription: {
    ...FONT_PROXIMA_12,
    color: GRAY_SEMIDARK,
    margin: 0,
  },
  chemicalDrawer: {
    flex: 1,
    display: 'flex',
    minHeight: 500,
    paddingBottom: 12,
  },
  searchOptions: {
    paddingTop: 12,
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
  searchOptionsContainer: {
    padding: '0 16px 16px',
  },
});
